import {
  filterProactiveGreetingPhrases,
  formatProactiveGreetingText,
  type ProactiveGreetingRequest,
  type ProactiveGreetingResponse,
} from '@teo/widget-shared';
import { logPopup } from '../popups/popup-logger.js';
import { getWidgetSession } from '../session/widget-session.js';
import { buildWidgetPage } from '../page-context/build-widget-page.js';

const GREETING_PATH = '/api/widget';

export class ProactiveGreetingError extends Error {
  readonly status: number;

  constructor(status: number, message?: string) {
    super(message ?? `[Teo Widget] Proactive greeting failed (${status})`);
    this.name = 'ProactiveGreetingError';
    this.status = status;
  }
}

export function buildProactiveGreetingUrl(apiBase: string, slug: string): string {
  const base = apiBase.replace(/\/$/, '');
  return `${base}${GREETING_PATH}/${slug}/greeting`;
}

export function createMockProactiveGreetingResponse(
  request: Pick<ProactiveGreetingRequest, 'locale' | 'page'>,
): ProactiveGreetingResponse {
  const title = request.page.title?.trim() || 'this page';
  const locale = (request.locale ?? 'en').slice(0, 2).toLowerCase();
  const availability = request.page.product?.availability;
  const confidence = request.page.product?.availability_confidence;
  const stockHint =
    confidence === 'high' && availability === 'out_of_stock'
      ? locale === 'uk'
        ? ' Бачу, зараз немає в наявності — підказати аналог?'
        : ' Looks like it is out of stock — want alternatives?'
      : '';

  const byLocale: Record<string, string[]> = {
    uk: [
      `Привіт! 👋`,
      request.page.type === 'home'
        ? 'Маєте питання? Напишіть у чат.'
        : `Бачу, вас цікавить «${title}».${stockHint || ' Підказати деталі?'}`,
    ],
    en: [
      `Hello! 👋`,
      request.page.type === 'home'
        ? 'Any questions? Start a chat.'
        : `I see you're interested in "${title}".${stockHint || ' Need help?'}`,
    ],
    de: [
      `Hallo! 👋`,
      request.page.type === 'home'
        ? 'Haben Sie Fragen? Schreiben Sie uns.'
        : `Sie interessieren sich für „${title}“. Kann ich helfen?`,
    ],
    fr: [
      `Bonjour ! 👋`,
      request.page.type === 'home'
        ? 'Des questions ? Écrivez-nous.'
        : `Vous vous intéressez à « ${title} ». Besoin d'aide ?`,
    ],
  };

  const phrases = byLocale[locale] ?? byLocale.en!;

  return {
    should_show: true,
    // Omit delay_seconds so client uses CONTEXT_POPUP_DELAY_BY_PAGE_TYPE
    // (cart/blog/… map correctly; greeting API page.type is only home|category|product|other).
    greeting: phrases.map((payload) => ({ type: 'text', payload })),
    products: [],
    locale: locale === 'uk' ? 'uk' : locale,
    source: 'fallback',
  };
}

export type FetchProactiveGreetingOptions = {
  mock?: boolean;
  signal?: AbortSignal;
};

export async function fetchProactiveGreeting(
  apiBase: string,
  slug: string,
  request: ProactiveGreetingRequest,
  options?: FetchProactiveGreetingOptions,
): Promise<ProactiveGreetingResponse> {
  if (options?.mock) {
    const response = createMockProactiveGreetingResponse(request);
    logPopup('greeting-api', 'mock response', {
      locale: request.locale,
      pageType: request.page.type,
      preview: formatProactiveGreetingText(response.greeting).slice(0, 80),
    });
    return response;
  }

  const url = buildProactiveGreetingUrl(apiBase, slug);

  logPopup('greeting-api', 'POST /api/widget/{slug}/greeting', {
    slug,
    pageType: request.page.type,
    pathname: request.page.pathname,
    locale: request.locale,
  });

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
    signal: options?.signal,
  });

  if (!response.ok) {
    logPopup(
      'greeting-api',
      'request failed',
      { status: response.status },
      'error',
    );
    throw new ProactiveGreetingError(response.status);
  }

  const data = (await response.json()) as ProactiveGreetingResponse;

  logPopup('greeting-api', 'response ok', {
    should_show: data.should_show,
    source: data.source,
    preview: formatProactiveGreetingText(data.greeting ?? []).slice(0, 80),
    products: Array.isArray(data.products) ? data.products.length : 0,
  });

  return data;
}

export type BuildProactiveGreetingRequestOptions = {
  url?: string;
  visitorId: string;
  sessionId?: number | null;
  locale: string;
  /** When false, only URL/pathname/title heuristics (no full parser). Default true. */
  pageContextEnabled?: boolean;
  /** When set, include `quick_reply_count` in the request body (chips mode). */
  quickReplyCount?: 1 | 2 | 3;
};

/** Build greeting body from live page context (async parser). */
export async function buildProactiveGreetingRequest(
  options: BuildProactiveGreetingRequestOptions,
): Promise<ProactiveGreetingRequest> {
  const page = await buildWidgetPage({
    url: options.url,
    pageContextEnabled: options.pageContextEnabled,
  });

  const request: ProactiveGreetingRequest = {
    visitor_id: options.visitorId,
    session_id: options.sessionId ?? getWidgetSession()?.sessionId ?? null,
    locale: options.locale,
    page,
  };

  if (options.quickReplyCount != null) {
    request.quick_reply_count = options.quickReplyCount;
  }

  return request;
}

export function greetingResponseToDisplayText(
  response: ProactiveGreetingResponse,
): string | null {
  if (!response.should_show) return null;
  const phrases = filterProactiveGreetingPhrases(response.greeting);
  if (phrases.length === 0) return null;
  const text = formatProactiveGreetingText(phrases);
  return text.trim() || null;
}
